/* eslint-disable babel/no-unused-expressions */
import { gql } from '@apollo/client/core';
import { executeQuery } from '@/components/commons/utils';
import Tracking from '@/components/commons/Tracking.js';

const SETS_CURATED_DATA_QUERY = gql`
  query setsCuratedData($hashKey: String!, $tag: String!) {
    curatedPageByKey(hashKey: $hashKey, tag: $tag) {
      curatedPage {
        attributes {
          setsPageHeroImageMobile
          setsPageHeroImageDesktop
        }
      }
    }
  }
`;

const PRODUCT_BUNDLES_QUERY = gql`
  query productBundles($page: Int, $count: Int, $rooms: [String]) {
    productBundles(page: $page, count: $count, rooms: $rooms) {
      availableRooms {
        name
        count
      }
      productBundles {
        id
        title
        description
        fileKey
        mobileFileKey
        discountPercentage
        productBundleProducts {
          isHeroProduct
          quantity
          productThumbnails {
            hashKey
            skuNo
            title
            slug
            imageUrl
            onSale
            onClearance
            price {
              currencyCode
              sale
              price
              retail
              clearance
              bundlePrice
            }
            variantImageUrl
          }
        }
        productBundleRooms
      }
    }
  }
`;

const PRODUCT_BUNDLE_QUERY = gql`
  query productBundle($bundleId: ID!) {
    productBundle(bundleId: $bundleId) {
      productBundle {
        id
        title
        description
        fileKey
        mobileFileKey
        discountPercentage
        slug
        productBundleProducts {
          isHeroProduct
          quantity
          productThumbnails {
            hashKey
            skuNo
            title
            slug
            imageUrl
            onSale
            onClearance
            price {
              currencyCode
              sale
              price
              retail
              clearance
              bundlePrice
            }
            variantImageUrl
          }
        }
        productBundleRooms
      }
    }
  }
`;

const PRODUCT_BUNDLE_PRODUCT_ID_QUERY = gql`
  query productBundleWithProductId($productId: ID!) {
    productBundleWithProductId(productId: $productId) {
      productBundles {
        id
        title
        description
        fileKey
        mobileFileKey
        discountPercentage
        productBundleRooms
        productBundleProducts {
          quantity
          isHeroProduct
          productThumbnails {
            hashKey
            skuNo
            title
            slug
            imageUrl
            onSale
            onClearance
            variantImageUrl
            price {
              currencyCode
              sale
              price
              retail
              clearance
              bundlePrice
            }
          }
        }
      }
    }
  }
`;

function parseProductBundle(bundle, productId = null) {
  const parsedBundleProducts = JSON.parse(JSON.stringify(bundle.productBundleProducts));

  parsedBundleProducts?.forEach((bundleProduct, index) => {
    if (!bundleProduct.activeVersion) {
      let initialIndex = bundleProduct.productThumbnails.findIndex((product) => product.hashKey === productId);
      initialIndex = Math.max(initialIndex, 0);

      parsedBundleProducts[index].activeVersion = bundleProduct.productThumbnails[initialIndex];
      parsedBundleProducts[index].activeVersion.productImage = `${parsedBundleProducts[index].activeVersion.imageUrl}?fit=max&w={width}&q={q}`;
    }
  });

  return {
    ...bundle,
    productBundleProducts: parsedBundleProducts,
  };
}

export default function useProductBundle() {
  async function loadProductBundles({ page = 0, count = 10, rooms = null }) {
    const result = await executeQuery(PRODUCT_BUNDLES_QUERY, {
      page,
      count,
      rooms,
    });

    if (result?.productBundles) {
      try {
        return {
          ...result.productBundles,
          productBundles: result.productBundles.productBundles.map((productBundle) => parseProductBundle(productBundle)),
        };
      } catch (error) {
        Tracking.sentryCaptureError(error);
      }
    }

    return null;
  }

  async function loadProductBundle(bundleId) {
    const result = await executeQuery(PRODUCT_BUNDLE_QUERY, { bundleId });

    if (result?.productBundle) {
      return {
        productBundle: parseProductBundle(result.productBundle.productBundle),
      };
    }

    return null;
  }

  async function loadProductBundleProductId(productId, shouldGetFirst = false) {
    if (!productId) {
      return null;
    }

    const result = await executeQuery(PRODUCT_BUNDLE_PRODUCT_ID_QUERY, {
      productId,
    });

    if (result?.productBundleWithProductId?.productBundles) {
      const parsedProductBundles = result.productBundleWithProductId.productBundles.map((productBundle) =>
        parseProductBundle(productBundle, productId),
      );

      if (shouldGetFirst) {
        return parsedProductBundles?.[0];
      }

      return parsedProductBundles;
    }

    return null;
  }

  async function loadPageCuratedContent() {
    const result = await executeQuery(SETS_CURATED_DATA_QUERY, {
      hashKey: 'BROWSE|bundle',
      tag: 'latest',
    });

    return result?.curatedPageByKey?.curatedPage ?? null;
  }

  return {
    loadProductBundles,
    loadProductBundle,
    loadProductBundleProductId,
    loadPageCuratedContent,
  };
}
